import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import CTABanner from "@/components/CTABanner";
import { localBusiness, GOOGLE_MAPS_URL, GOOGLE_RATING, ADDRESS_LINE } from "@/lib/business";

// Production posts SAME-ORIGIN (/api/contact on whatever host served the page)
// so every submission passes through Cloudflare's rate-limit rule on the custom
// domain. Posting to the *.web.app host skipped it entirely. Local dev still
// uses REACT_APP_BACKEND_URL.
const BACKEND_URL = process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    // Honeypot. Rendered off-screen and aria-hidden below, so a real person
    // never sees or tabs into it — anything in here is a bot. The backend
    // treats it as advisory and only acts on a NON-EMPTY value, so this can
    // never reject a genuine submission.
    website_hp: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const services = [
    "Pre-Sale Property Inspection",
    "Building Status Report",
    "Building Condition Report",
    "Pool Report",
    "Plumbing Report",
    "Electrical Report (via partners)",
    "General Enquiry"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send where the enquiry came from alongside it. A lead that arrives
      // knowing which page prompted it is worth far more on the callback than
      // one that just says "website", and it is the only way to tell which
      // service page is actually converting.
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        source_page: typeof window !== "undefined" ? window.location.pathname : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "", website_hp: "" });
    } catch (error) {
      console.error("Failed to submit form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Contact Us | Book Building Inspection Darwin | Aussie Inspections</title>
        <meta name="description" content="Contact Aussie Inspections for building, pest, pool and electrical inspections in Darwin NT. Call 0413 245 519 or book online. Fast response, local experts." />
        <link rel="canonical" href="https://aussieinspections.com.au/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Aussie Inspections | Book Your Inspection Today" />
        <meta property="og:description" content="Book your building inspection in Darwin. Call 0413 245 519 or fill out our contact form for fast, professional service." />
        <meta property="og:url" content="https://aussieinspections.com.au/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "url": "https://aussieinspections.com.au/contact",
            "name": "Contact Aussie Inspections",
            "description":
              "Book a building, pool or plumbing inspection in Darwin, Palmerston, Litchfield or Wagait.",
            "mainEntity": localBusiness({
              areaServed: [
                { "@type": "City", "name": "Darwin" },
                { "@type": "City", "name": "Palmerston" },
                { "@type": "AdministrativeArea", "name": "Litchfield" },
                { "@type": "AdministrativeArea", "name": "Wagait" }
              ]
            })
          })}
        </script>
      </Helmet>

      {/* CTA Banner */}
      <CTABanner />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none mb-4">
              Get In Touch
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Book your inspection or ask us a question
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-chivo text-2xl font-bold text-slate-900 mb-6">
                  Contact Information
                </h2>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <a
                        href="tel:+61413245519"
                        className="text-slate-600 hover:text-primary"
                        data-testid="contact-phone"
                      >
                        0413 24 55 19
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a
                        href="mailto:info@aussieinspections.com.au"
                        className="text-slate-600 hover:text-primary"
                        data-testid="contact-email"
                      >
                        info@aussieinspections.com.au
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Office &amp; Service Area</h3>
                      <p className="text-slate-600 mb-1" data-testid="contact-address">
                        {ADDRESS_LINE}
                      </p>
                      <p className="text-slate-600 mb-4">
                        Servicing Darwin, Palmerston, Litchfield, and surrounding NT areas
                      </p>
                      <div className="rounded-lg overflow-hidden border border-slate-200">
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d247089.94729498945!2d130.68811073267045!3d-12.42528858180498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2cc0900d5b35ac5d%3A0x400f6a2e5c8c850!2sDarwin%20NT!5e0!3m2!1sen!2sau!4v1735027200000!5m2!1sen!2sau"
                          width="100%"
                          height="200"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Darwin service area map"
                        ></iframe>
                      </div>
                      {/* The embed above is the service area, not a shopfront.
                          This link is the verified Google Business Profile —
                          the one signal that reconciles this site with it. */}
                      <a
                        href={GOOGLE_MAPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-3 text-sm font-medium text-secondary hover:underline"
                      >
                        Find us on Google — rated {GOOGLE_RATING.value} from{" "}
                        {GOOGLE_RATING.count} reviews
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Clock className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                      <p className="text-slate-600">
                        Monday - Friday: 8:00 AM - 5:00 PM<br />
                        Saturday & Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                <h3 className="font-chivo font-bold text-slate-900 mb-3">
                  Why Choose Us?
                </h3>
                <ul className="space-y-2">
                  {[
                    "Licensed & Insured",
                    "Detailed Reports in 24 Hours",
                    "Latest Technology",
                    "Local NT Experts"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-50 p-8 rounded-lg border border-slate-200"
              >
                <h2 className="font-chivo text-2xl font-bold text-slate-900 mb-6">
                  Request a Quote or Book an Inspection
                </h2>

                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-secondary mx-auto mb-4" />
                    <h3 className="font-chivo text-2xl font-bold text-slate-900 mb-2">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 mb-6">
                      We&apos;ve received your enquiry and will be in touch within 24 hours.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      data-testid="submit-another-button"
                    >
                      Submit Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="name-input"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          data-testid="phone-input"
                          placeholder="0400 123 456"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="email-input"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service">Service Required *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => setFormData({ ...formData, service: value })}
                        required
                      >
                        <SelectTrigger data-testid="service-select">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message or Property Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        data-testid="message-input"
                        rows={6}
                        placeholder="Tell us about your property and inspection needs..."
                      />
                    </div>

                    {/* Honeypot. Off-screen rather than display:none (which a
                        number of bots skip), removed from the accessibility
                        tree and the tab order, and excluded from autofill, so
                        no human and no screen reader ever reaches it. */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-9999px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden"
                      }}
                    >
                      {/* No visible label text on purpose: this page is
                          prerendered, so any text here lands in the crawlable
                          HTML and llms-full.txt. A bot targets the field NAME,
                          which is what makes the trap work. */}
                      <input
                        aria-label="Leave empty"
                        id="website_hp"
                        name="website_hp"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_hp}
                        onChange={handleChange}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase"
                      data-testid="submit-button"
                    >
                      {loading ? "Submitting..." : "Send Enquiry"}
                    </Button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
